'use client'

import React, { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowLeft, Gear, Users, Clock, TrendUp, CheckCircle, Lightning, Target, ChartBar, Package, ShoppingCart, Warning, Brain, Shield } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'
// Use public assets paths for static hosting

export default function SupplyChainOptimization() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  return (
    <Components.PageContainer>
      <Components.GlassNavigation />
      
      <main className="pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">

          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-[var(--brutal-ink)] mb-6 leading-tight">
              Supply Chain Optimization
            </h1>
            <p className="text-xl text-[var(--brutal-muted)] mb-8 leading-relaxed max-w-4xl mx-auto">
              Rule-engine application that automated supply chain management and inventory planning through intelligent strategies, generating optimized plans with outcome previews and tactical integration for override adjustments.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Rule Engine</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Supply Chain</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Oracle Redwood</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Strategic Leadership</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Inventory Planning</Badge>
            </div>
          </div>

          {/* Oracle Interface Preview Carousel */}
          <div className="mb-16">
            <Card variant="media" className="brutal-panel overflow-hidden !gap-0 !py-0">
              <Components.ImageCarousel
                images={[
                  {
                    src: "/assets/oracle-inventory-dashboard.png",
                    alt: "Oracle inventory planning dashboard showing comprehensive supply chain data visualization with stacked bar charts, demand forecasting, and tactical planning interface",
                    title: "Oracle Inventory Planning Dashboard",
                    description: "Main dashboard with comprehensive supply chain data visualization"
                  },
                  {
                    src: "/assets/oracle-rule-engine-config.png",
                    alt: "Oracle rule engine configuration interface showing automation strategy setup and parameter controls",
                    title: "Rule Engine Configuration",
                    description: "Intelligent rule configuration interface for automation strategies"
                  },
                  {
                    src: "/assets/oracle-demand-forecasting.png",
                    alt: "Oracle demand forecasting visualization with predictive analytics and trend analysis",
                    title: "Demand Forecasting",
                    description: "Advanced forecasting with predictive analytics and outcome modeling"
                  },
                  {
                    src: "/assets/oracle-tactical-integration.png",
                    alt: "Oracle tactical planning integration showing seamless workflow between automated and manual planning",
                    title: "Tactical Integration",
                    description: "Seamless integration with tactical planning tools for override adjustments"
                  }
                ]}
              />
              <div className="p-4 md:p-6 text-center">
                <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4">Oracle Inventory Planning Interface</h3>
                <p className="text-[var(--brutal-muted)] mb-4">Enterprise-grade supply chain optimization with Oracle Redwood design system implementation</p>
                <div className="grid grid-cols-2 gap-4 text-left text-[var(--brutal-muted)] text-sm max-w-md mx-auto">
                  <div>• Rule-based automation strategies</div>
                  <div>• Demand forecasting visualization</div>
                  <div>• Tactical planning integration</div>
                  <div>• Real-time outcome previews</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Key Metrics */}
          <div className="grid sm:grid-cols-4 grid-cols-2 gap-4 md:gap-6 mb-12 sm:max-w-6xl max-w-2xl mx-auto">
            <Card className="brutal-panel p-4 md:p-6 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[#8fce8f]">
                <TrendUp className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)] flex-shrink-0" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2 break-words">$50M+</div>
              <div className="text-[var(--brutal-muted)] text-xs md:text-sm break-words">Cost Savings</div>
            </Card>
            
            <Card className="brutal-panel p-4 md:p-6 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[var(--brutal-kraft)]">
                <Shield className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)] flex-shrink-0" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2 break-words">85%</div>
              <div className="text-[var(--brutal-muted)] text-xs md:text-sm break-words">Stockout Reduction</div>
            </Card>
            
            <Card className="brutal-panel p-4 md:p-6 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[var(--brutal-mustard)]">
                <Clock className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)] flex-shrink-0" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2 break-words">70%</div>
              <div className="text-[var(--brutal-muted)] text-xs md:text-sm break-words">Planning Time Saved</div>
            </Card>
            
            <Card className="brutal-panel p-4 md:p-6 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[var(--brutal-accent)]">
                <Users className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)] flex-shrink-0" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2 break-words">500+</div>
              <div className="text-[var(--brutal-muted)] text-xs md:text-sm break-words">Retail Locations</div>
            </Card>
          </div>

          {/* Project Overview */}
          <Tabs defaultValue="overview" className="mb-12">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview" className="truncate px-1 sm:px-3">Overview & Strategy</TabsTrigger>
              <TabsTrigger value="solution" className="truncate px-1 sm:px-3">Rule Engine Solution</TabsTrigger>
              <TabsTrigger value="results" className="truncate px-1 sm:px-3">Results & Impact</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="mt-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <Card className="brutal-panel p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-[var(--brutal-panel)] rounded-full mr-3" aria-hidden="true"></div>
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)]">The Challenge</h3>
                  </div>
                  <p className="text-[var(--brutal-muted)] leading-relaxed">
                    Retailers faced massive supply chain disruptions during COVID-19, experiencing critical shortages of essential items like toilet paper and water. Manual inventory planning processes were too slow and reactive to handle volatile demand patterns, resulting in significant lost sales and customer dissatisfaction across hundreds of store locations.
                  </p>
                </Card>
                
                <Card className="brutal-panel p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-[var(--brutal-panel)] rounded-full mr-3" aria-hidden="true"></div>
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)]">Strategic Vision</h3>
                  </div>
                  <p className="text-[var(--brutal-muted)] leading-relaxed">
                    Create an intelligent rule-engine system that would automate supply chain decisions through configurable strategies, provide predictive outcome modeling, and integrate seamlessly with tactical planning tools. The goal was to transform reactive inventory management into proactive, data-driven optimization that could adapt to market volatility in real-time.
                  </p>
                </Card>
              </div>

              <Components.KeyDecisions
                decisions={[
                  {
                    question: 'Keep planners in the loop, or fully automate replenishment?',
                    tension:
                      'Full automation would have moved inventory faster. COVID demand swung hard enough that a wrong auto-decision emptied shelves or buried stores in the wrong stock.',
                    choice:
                      'We built a rule engine with predicted outcomes, then let planners review and override before commits hit the network.',
                  },
                  {
                    question: 'Hard-code strategies, or make rules configurable?',
                    tension:
                      'Hard-coded logic ships faster for one crisis. Retailers needed strategies they could retune when the next shortage pattern showed up.',
                    choice:
                      'We designed configurable strategies so teams could model outcomes and adapt without a release cycle for every shock.',
                  },
                  {
                    question: 'Stay on legacy UI patterns, or adopt Oracle Redwood?',
                    tension:
                      'Legacy patterns were familiar to the existing team. They also locked the product into an outdated enterprise look that fought every new workflow.',
                    choice:
                      'We introduced Redwood so the rule-engine work sat on a modern system instead of extending a UI that was already past its shelf life.',
                  },
                ]}
              />
              
              <Card className="brutal-panel p-8">
                <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-6">Strategic Leadership & Design Process</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 aspect-square brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">1</div>
                    <div>
                      <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Co-Led Strategic Vision</h4>
                      <p className="text-[var(--brutal-muted)]">Partnered with Product Strategy to define the rule-engine approach, establishing the framework for automated supply chain optimization and outcome prediction modeling.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 aspect-square brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">2</div>
                    <div>
                      <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Oracle Redwood Implementation</h4>
                      <p className="text-[var(--brutal-muted)]">Introduced and implemented Oracle's new Redwood design system, establishing modern design patterns and ensuring consistency across the enterprise platform.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 aspect-square brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">3</div>
                    <div>
                      <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Cross-Functional Direction</h4>
                      <p className="text-[var(--brutal-muted)]">Directed product owners and development teams through the implementation process, ensuring alignment between strategic vision and technical execution.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 aspect-square brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">4</div>
                    <div>
                      <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Integration Strategy</h4>
                      <p className="text-[var(--brutal-muted)]">Designed seamless integration with tactical Inventory Planning solutions, enabling users to review automated decisions and make strategic override adjustments when needed.</p>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>
            
            <TabsContent value="solution" className="mt-8">
              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              
              <div className="mb-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Rule-Engine Architecture</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <Brain className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Intelligent Rule Configuration</h4>
                        <p className="text-sm md:text-base text-[var(--brutal-ink)] mb-4">User-friendly interface for setting up complex supply chain rules and automation strategies based on demand patterns, seasonality, and business constraints</p>
                        <div className="flex items-center text-[var(--brutal-ink)] text-sm">
                          <TrendUp className="w-4 h-4 mr-2" />
                          90% reduction in manual planning
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <ChartBar className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Outcome Prediction</h4>
                        <p className="text-sm md:text-base text-[var(--brutal-ink)] mb-4">Advanced modeling that previews the impact of rule-based strategies before execution, showing projected inventory levels and potential outcomes</p>
                        <div className="flex items-center text-[var(--brutal-ink)] text-sm">
                          <TrendUp className="w-4 h-4 mr-2" />
                          95% forecast accuracy improvement
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <Gear className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Tactical Integration</h4>
                        <p className="text-sm md:text-base text-[var(--brutal-ink)] mb-4">Seamless connection with existing Inventory Planning tools, allowing planners to review automated decisions and make strategic adjustments</p>
                        <div className="flex items-center text-[var(--brutal-ink)] text-sm">
                          <TrendUp className="w-4 h-4 mr-2" />
                          100% system integration achieved
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <Warning className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Exception Management</h4>
                        <p className="text-sm md:text-base text-[var(--brutal-ink)] mb-4">Intelligent identification and flagging of supply chain issues, enabling planners to pinpoint problems and adjust strategies in real-time</p>
                        <div className="flex items-center text-[var(--brutal-ink)] text-sm">
                          <TrendUp className="w-4 h-4 mr-2" />
                          80% faster issue resolution
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              
              {/* Oracle Redwood Design System */}
              <div className="mb-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Oracle Redwood Design System</h3>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="backdrop-blur-xl bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-xl p-6 text-center">
                    <Gear className="w-12 h-12 text-[var(--brutal-ink)] mx-auto mb-4" />
                    <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Modern Interface Design</h4>
                    <p className="text-sm md:text-base text-[var(--brutal-ink)]">Introduced Oracle's latest design language with clean, intuitive interfaces optimized for complex enterprise workflows</p>
                  </div>
                  
                  <div className="backdrop-blur-xl bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-xl p-6 text-center">
                    <Target className="w-12 h-12 text-[var(--brutal-ink)] mx-auto mb-4" />
                    <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Consistent Patterns</h4>
                    <p className="text-sm md:text-base text-[var(--brutal-ink)]">Established unified design patterns across the entire supply chain platform, improving user experience and reducing learning curve</p>
                  </div>
                  
                  <div className="backdrop-blur-xl bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-xl p-6 text-center">
                    <Lightning className="w-12 h-12 text-[var(--brutal-ink)] mx-auto mb-4" />
                    <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Enterprise Scalability</h4>
                    <p className="text-sm md:text-base text-[var(--brutal-ink)]">Designed for enterprise-scale deployment with accessibility compliance and performance optimization for large retail operations</p>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="results" className="mt-8">
              <Card className="brutal-panel p-4 md:p-8 mb-8">
                <h3 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6">Business Impact & Results</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-4 border-b border-[var(--brutal-ink)]">
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-semibold text-[var(--brutal-ink)] mb-1">Cost Savings</h4>
                      <p className="text-sm text-[var(--brutal-muted)]">Annual operational savings</p>
                    </div>
                    <div className="text-base md:text-2xl font-bold text-[var(--brutal-ink)]">$2M+</div>
                  </div>
                  
                  <div className="flex items-center justify-between py-4 border-b border-[var(--brutal-ink)]">
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-semibold text-[var(--brutal-ink)] mb-1">Planning Time</h4>
                      <p className="text-sm text-[var(--brutal-muted)]">Faster inventory planning</p>
                    </div>
                    <div className="text-base md:text-2xl font-bold text-[var(--brutal-ink)]">30% reduction</div>
                  </div>
                  
                  <div className="flex items-center justify-between py-4 border-b border-[var(--brutal-ink)]">
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-semibold text-[var(--brutal-ink)] mb-1">Stockout Events</h4>
                      <p className="text-sm text-[var(--brutal-muted)]">Improved availability</p>
                    </div>
                    <div className="text-base md:text-2xl font-bold text-[var(--brutal-ink)]">50% reduction</div>
                  </div>
                  
                  <div className="flex items-center justify-between py-4">
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-semibold text-[var(--brutal-ink)] mb-1">Reduction in Lost Sales</h4>
                      <p className="text-sm text-[var(--brutal-muted)]">Better revenue capture</p>
                    </div>
                    <div className="text-base md:text-2xl font-bold text-[var(--brutal-ink)]">10% improvement</div>
                  </div>
                </div>
              </Card>

              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              
              {/* Key Achievements */}
              <div className="mb-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Key Achievements</h3>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <h4 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)] mb-4">Supply Chain Improvements</h4>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[var(--brutal-ink)]" />
                        <span className="text-sm md:text-base text-[var(--brutal-ink)]">Solved COVID-19 supply chain crisis issues</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[var(--brutal-ink)]" />
                        <span className="text-sm md:text-base text-[var(--brutal-ink)]">$2M+ annual cost savings achieved</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[var(--brutal-ink)]" />
                        <span className="text-sm md:text-base text-[var(--brutal-ink)]">50% reduction in stockout events</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[var(--brutal-ink)]" />
                        <span className="text-sm md:text-base text-[var(--brutal-ink)]">30% reduction in manual planning time</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <h4 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)] mb-4">Strategic Leadership Wins</h4>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[var(--brutal-ink)]" />
                        <span className="text-sm md:text-base text-[var(--brutal-ink)]">Co-led strategic vision with Product Strategy</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[var(--brutal-ink)]" />
                        <span className="text-sm md:text-base text-[var(--brutal-ink)]">Successfully introduced Oracle Redwood design system</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[var(--brutal-ink)]" />
                        <span className="text-sm md:text-base text-[var(--brutal-ink)]">Directed cross-functional development teams</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[var(--brutal-ink)]" />
                        <span className="text-sm md:text-base text-[var(--brutal-ink)]">Achieved seamless tactical system integration</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Key Learnings */}
          <div className="mb-12">
            {/* Subtle divider */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
            
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Key Learnings</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="backdrop-blur-xl bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-xl p-6">
                <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Strategic Co-Leadership</h4>
                <p className="text-sm md:text-base text-[var(--brutal-ink)]">Collaborating closely with Product Strategy from the beginning ensures alignment between user experience goals and business objectives, leading to more successful outcomes.</p>
              </div>
              
              <div className="backdrop-blur-xl bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-xl p-6">
                <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Design System Impact</h4>
                <p className="text-sm md:text-base text-[var(--brutal-ink)]">Introducing a new design system like Oracle Redwood during a major product launch creates consistency and sets the foundation for future platform evolution.</p>
              </div>
              
              <div className="backdrop-blur-xl bg-[var(--brutal-kraft)] border border-[var(--brutal-ink)] rounded-xl p-6">
                <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Integration is Critical</h4>
                <p className="text-sm md:text-base text-[var(--brutal-ink)]">Seamless integration with existing tactical tools is essential for user adoption - automation should enhance, not replace, human decision-making capabilities.</p>
              </div>
            </div>
          </div>

          {/* Call-to-Action */}
          <Card className="brutal-panel p-8 text-center">
            <h3 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">Ready to optimize your supply chain operations?</h3>
            <p className="text-[var(--brutal-muted)] mb-8 max-w-3xl mx-auto text-lg">
              I help organizations design intelligent automation solutions that transform reactive processes into proactive, data-driven optimization strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                variant="outline" 
                className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] hover:text-[var(--brutal-ink)]"
              >
                <Link to="/ProfessionalProjects">View More Projects</Link>
              </Button>
              <Button 
                asChild 
                className="brutal-btn text-[var(--brutal-ink)] border-0"
              >
                <Link to="/Contact">Get In Touch</Link>
              </Button>
            </div>
          </Card>
        </div>
      </main>

      {/* Image Modal */}
      {selectedImage && (
        <Components.ImageModal
          src={selectedImage}
          alt="Supply Chain Optimization"
        />
      )}
    </Components.PageContainer>
  )
}